import {
  ConstrainMode,
  getTheme,
  IColumn,
  IContextualMenuItem,
  IContextualMenuProps,
  PrimaryButton,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
  Stack
} from "@fluentui/react";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { BaseListMetaData } from "../api/react-query/hook/use-get/interface/list-meta-data.interface";
import { HeaderItemCount } from "../list-data/components/headerItemCount";
import { ListDataFilters } from "../list-data/components/listDataFilters";
import { DEFAULT_ITEMS_PER_PAGE } from "../list-data/lib/constants";
import { FiltersProps } from "../list-data/lib/filters/interfaces";
import { useListFilters } from "../list-data/lib/hooks/useListFilters";
import { listStyles } from "../list-data/lib/styles";
import { insertActionsIcons } from "../list-data/lib/utils/insertActionsIcons";

import { NoDataFound } from "./components/NoDataFound";
import { TableCommandBar } from "./components/TableCommandBar";
import { TableShimmer } from "./components/TableShimmer";

import { FilterParamsType } from "@/common/types/ListFromApi.types";
import { useURLFilter } from "@/hooks/useUrlFilter";

interface TableProps<T> {
  columns: Array<IColumn>;
  list: BaseListMetaData<T>;
  isLoadingList: boolean;
  primaryButtonText: string;
  menuItems?: Array<IContextualMenuItem>;
  headerAction?: () => void;
  setSelectedItemId?: (selectedItemId: number) => void;
  headerFilters?: HeaderFilters;
  filters?: Array<FiltersProps>;
  isMassActionModeEnabled?: boolean;
  massActions?: Array<HeaderMassAction>;
  setIsMassActionModeEnabled?: Dispatch<SetStateAction<boolean>>;
  selectedItemsForMassAction?: Array<number>;
  setSelectedItemsForMassAction?: Dispatch<SetStateAction<Array<number>>>;
  headerCustomActions?: Array<HeaderCustomAction>;
}

interface HeaderFilters {
  filtersConfig: Array<{
    text: string;
    queryValue: string;
    count: string;
    iconName?: string;
  }>;
  filterQuery: string;
}

interface HeaderCustomAction {
  key: string;
  buttonText: string;
  icon?: string;
  onClick: () => void;
}

interface HeaderMassAction {
  key: string;
  buttonText: string;
  buttonIcon?: string;
  onInitialize: () => void;
}

interface RenderHeaderActionsParams {
  shouldShowMassActionButtons: boolean;
  isMassActionModeEnabled: boolean;
  handleCancelMassActionMode: () => void;
  handleStartMassActionMode: () => void;
  headerAction?: () => void;
  headerCustomActions?: Array<HeaderCustomAction>;
  massActions: Array<HeaderMassAction>;
  primaryButtonText: string;
}

interface RenderHeaderSummaryParams {
  totalItems: number;
  selectedCount: number;
}

const theme = getTheme();

function renderMassActionButtons(
  massActions: Array<HeaderMassAction>
): Array<JSX.Element> {
  return massActions.map(massAction => (
    <PrimaryButton
      key={massAction.key}
      id={`mass-action-init-btn-${massAction.key}`}
      iconProps={
        massAction.buttonIcon ? { iconName: massAction.buttonIcon } : undefined
      }
      text={massAction.buttonText}
      onClick={massAction.onInitialize}
    />
  ));
}

function renderHeaderActions({
  shouldShowMassActionButtons,
  isMassActionModeEnabled,
  handleCancelMassActionMode,
  handleStartMassActionMode,
  headerAction,
  massActions,
  primaryButtonText,
  headerCustomActions = []
}: RenderHeaderActionsParams): JSX.Element {
  const massActionModeButtonStyle = isMassActionModeEnabled
    ? {
        backgroundColor: theme.palette.teal,
        borderColor: theme.palette.teal
      }
    : undefined;

  function massActionButtons() {
    return (
      <>
        {shouldShowMassActionButtons && renderMassActionButtons(massActions)}

        {!isMassActionModeEnabled && (
          <PrimaryButton
            id="mass-actions-toggle-btn"
            iconProps={{ iconName: "BulletedList2" }}
            text="Ações em Massa"
            onClick={handleStartMassActionMode}
          />
        )}

        {isMassActionModeEnabled && (
          <PrimaryButton
            id="mass-actions-toggle-btn"
            iconProps={{ iconName: "BoxMultiplySolid" }}
            text="Cancelar Ações em Massa"
            onClick={handleCancelMassActionMode}
            style={massActionModeButtonStyle}
          />
        )}
      </>
    );
  }

  return (
    <Stack horizontal tokens={{ childrenGap: 8 }}>
      {massActions.length > 0 && massActionButtons()}

      {headerCustomActions.map(headerCustomAction => (
        <PrimaryButton
          key={headerCustomAction.key}
          id={`header-custom-action-${headerCustomAction.key}`}
          iconProps={
            headerCustomAction.icon
              ? { iconName: headerCustomAction.icon }
              : undefined
          }
          text={headerCustomAction.buttonText}
          onClick={headerCustomAction.onClick}
          disabled={isMassActionModeEnabled}
        />
      ))}

      {headerAction && (
        <PrimaryButton
          id={`add-${primaryButtonText?.replace(/ /g, "-")}`}
          iconProps={{ iconName: "Add" }}
          text={primaryButtonText}
          onClick={headerAction}
          disabled={isMassActionModeEnabled}
        />
      )}
    </Stack>
  );
}

function renderHeaderSummary({
  totalItems,
  selectedCount
}: RenderHeaderSummaryParams): JSX.Element {
  return (
    <Fragment>
      Total: {totalItems}
      {Boolean(selectedCount) && (
        <Fragment> | Selecionados: {selectedCount}</Fragment>
      )}
    </Fragment>
  );
}

export function Table<T>({
  list,
  isLoadingList,
  headerAction,
  primaryButtonText,
  isMassActionModeEnabled = false,
  setIsMassActionModeEnabled,
  massActions = [],
  selectedItemsForMassAction = [],
  setSelectedItemsForMassAction,
  headerFilters,
  filters,
  setSelectedItemId,
  menuItems,
  columns,
  headerCustomActions
}: TableProps<T>): JSX.Element {
  const {
    selectedKeys,
    setSelectedKeys,
    selectedTerms,
    setSelectedTerms,
    visibleTerms,
    setVisibleTerms,
    bulkOptionsFilters,
    debouncedTerms,
    defaultOptionsFilters
  } = useListFilters(filters);

  const itemsPerPage = Number(
    defaultOptionsFilters.limit || DEFAULT_ITEMS_PER_PAGE
  );

  const [isFilterVisible, setIsFilterVisible] = useState<boolean>(true);

  const toggleFilterVisibility = useCallback(() => {
    setIsFilterVisible(prevValue => !prevValue);
  }, []);

  const { changeUrlParams } = useURLFilter();

  const paginate = useCallback(
    (filterParams?: FilterParamsType) => {
      changeUrlParams(filterParams);
    },
    [changeUrlParams]
  );

  const paginateRef = useRef(paginate);

  const stablePaginate = useCallback((params?: FilterParamsType) => {
    paginateRef.current?.(params);
  }, []);

  const menuProps: IContextualMenuProps = useMemo(
    () => ({
      shouldFocusOnMount: true,
      items: menuItems
    }),
    [menuItems]
  );

  const isListReady = list !== undefined;

  const shouldDisplayNoDataFound =
    isListReady && !isLoadingList && list.meta.totalItems === 0;

  const filtersAreStabilizing = useRef(true);

  const selectionRef = useRef<Selection>(null!);
  const hasMassActions = massActions.length > 0;

  if (!selectionRef.current && hasMassActions) {
    selectionRef.current = new Selection({
      onSelectionChanged: () => {
        const selectedItems = selectionRef.current.getSelection() as Array<{
          id: number;
        }>;
        const selectedIds = selectedItems.map(item => item.id);

        if (setSelectedItemsForMassAction) {
          setSelectedItemsForMassAction(selectedIds);
        }
      },
      canSelectItem: () => true,
      selectionMode: SelectionMode.multiple
    });
  }

  const selection = selectionRef.current;

  const handleClearSelectedItems = useCallback(() => {
    if (selection) {
      selection.setAllSelected(false);
    }

    if (setSelectedItemsForMassAction) {
      setSelectedItemsForMassAction([]);
    }
  }, [selection, setSelectedItemsForMassAction]);

  const handleStartMassActionMode = useCallback(() => {
    handleClearSelectedItems();
    setIsMassActionModeEnabled?.(true);
  }, [handleClearSelectedItems, setIsMassActionModeEnabled]);

  const handleCancelMassActionMode = useCallback(() => {
    handleClearSelectedItems();
    setIsMassActionModeEnabled?.(false);
  }, [handleClearSelectedItems, setIsMassActionModeEnabled]);

  const filteredColumns = useMemo(() => {
    if (isMassActionModeEnabled) {
      return columns.filter(column => column.key !== "actions");
    }

    return insertActionsIcons({
      columns,
      menuProps,
      selectItem: (item: { id: number }) => setSelectedItemId?.(item.id)
    });
  }, [isMassActionModeEnabled, columns, menuProps, setSelectedItemId]);

  const shouldShowMassActionButtons =
    isMassActionModeEnabled && Boolean(selectedItemsForMassAction.length);

  useEffect(() => {
    paginateRef.current = paginate;
  }, [paginate]);

  useEffect(() => {
    if (filtersAreStabilizing.current) {
      filtersAreStabilizing.current = false;

      return;
    }

    const filters = { ...debouncedTerms };

    for (const key in filters) {
      if (typeof filters[key] === "string") {
        filters[key] = filters[key].trim();
      }
    }

    stablePaginate({
      limit: itemsPerPage,
      ...selectedKeys,
      ...filters,
      ...bulkOptionsFilters
    });
  }, [
    debouncedTerms,
    bulkOptionsFilters,
    itemsPerPage,
    selectedKeys,
    stablePaginate
  ]);

  if (!isListReady && isLoadingList) {
    return <TableShimmer columns={columns} />;
  }

  const shouldRenderHeaderActions = Boolean(
    headerAction || hasMassActions || headerCustomActions?.length
  );

  return (
    <Fragment>
      <Stack
        horizontal
        horizontalAlign="space-between"
        styles={{ root: { padding: 12 } }}
      >
        <Stack.Item align="center">
          {renderHeaderSummary({
            totalItems: list?.meta?.totalItems ?? 0,
            selectedCount: selectedItemsForMassAction.length
          })}
        </Stack.Item>

        <Stack.Item
          align="center"
          style={{
            justifyContent: "center",
            display: "flex"
          }}
        >
          {shouldRenderHeaderActions &&
            renderHeaderActions({
              shouldShowMassActionButtons,
              isMassActionModeEnabled,
              handleCancelMassActionMode,
              handleStartMassActionMode,
              headerAction,
              primaryButtonText,
              massActions,
              headerCustomActions
            })}
        </Stack.Item>
      </Stack>

      {headerFilters && (
        <Stack horizontalAlign="center">
          <HeaderItemCount
            selectedKeys={selectedKeys}
            handleLinkClick={(currentKeys: Record<string, string>) => {
              setSelectedKeys(currentKeys);
              paginate({
                limit: itemsPerPage,
                ...debouncedTerms,
                ...currentKeys
              });
            }}
            filterQuery={headerFilters.filterQuery}
            filtersConfig={headerFilters.filtersConfig}
          />
        </Stack>
      )}

      <TableCommandBar
        meta={list?.meta}
        hasFilters={Boolean(filters)}
        onPageChange={page => {
          stablePaginate({
            limit: itemsPerPage,
            ...debouncedTerms,
            ...selectedKeys,
            page
          });
        }}
        onToggleFilters={toggleFilterVisibility}
        onResetFilters={() => {
          setSelectedKeys({});
          setSelectedTerms({});
          setVisibleTerms({});
          paginate({ limit: DEFAULT_ITEMS_PER_PAGE });
        }}
      />

      {isFilterVisible && (
        <ListDataFilters
          setSelectedKeys={setSelectedKeys}
          setVisibleTerms={setVisibleTerms}
          setSelectedTerms={setSelectedTerms}
          toggleFilterVisibility={toggleFilterVisibility}
          filters={filters}
          _paginate={stablePaginate}
          itemsPerPage={itemsPerPage}
          visibleTerms={visibleTerms}
          selectedKeys={selectedKeys}
          selectedTerms={selectedTerms}
          debouncedTerms={debouncedTerms}
          isCloseFilterButtonDisabled={false}
          isItemsPerPageFilterDisabled={false}
        />
      )}

      <ShimmeredDetailsList
        setKey="items"
        items={list?.items ?? []}
        columns={filteredColumns}
        selection={hasMassActions ? selection : undefined}
        selectionPreservedOnEmptyClick={true}
        ariaLabelForSelectionColumn="Toggle selection"
        ariaLabelForSelectAllCheckbox="Toggle selection for all items"
        checkButtonAriaLabel="Row checkbox"
        constrainMode={ConstrainMode.horizontalConstrained}
        enableShimmer={isLoadingList}
        selectionMode={
          isMassActionModeEnabled ? SelectionMode.multiple : SelectionMode.none
        }
        enterModalSelectionOnTouch={true}
        detailsListStyles={listStyles}
      />

      {shouldDisplayNoDataFound && <NoDataFound />}
    </Fragment>
  );
}
